// Groups list (SPEC §7.3): name, course, current main teacher, room + center, slots; filter; show archived.

import type { ApiResult } from "../../api/client";
import { fetchGroups } from "../../api/groups";
import type { Group } from "../../api/types";
import { t } from "../../i18n/i18n";
import { navigate, setRedraw, type RouteMatch } from "../../router";
import { isAdmin } from "../../session";
import { createNameComparer, matchesSearch } from "../../text";
import { currentLocale } from "../../i18n/i18n";
import { createButton, createElement, createLink } from "../../ui/dom";
import { createCheckbox, createInputField, type Field } from "../../ui/forms";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import {
  classroomWithCenter, courseName, describeSlots, loadReferenceData, teacherName, type ReferenceData,
} from "../referenceData";

export async function renderGroupsListPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const includeArchived: boolean = route.query.get("archived") === "1";
  const page: HTMLElement = startPage(container, t("groups.title"));
  page.append(createLoading());
  const groups: ApiResult<Group[]> = await fetchGroups(includeArchived);
  const reference: ApiResult<ReferenceData> = await loadReferenceData();
  if (groups.ok === false) {
    showPageProblem(page, groups.problem);
    return;
  }

  if (reference.ok === false) {
    showPageProblem(page, reference.problem);
    return;
  }

  let filter: string = route.query.get("q") ?? "";
  const draw = (): void => {
    const freshPage: HTMLElement = startPage(container, t("groups.title"));
    drawList(freshPage, { groups: groups.data, reference: reference.data, includeArchived, filter,
      onFilter: (value: string): void => {
        filter = value;
      } });
  };
  setRedraw(container, draw);
  draw();
}

interface ListView {
  groups: Group[];
  reference: ReferenceData;
  includeArchived: boolean;
  filter: string;
  onFilter: (value: string) => void;
}

function drawList(page: HTMLElement, view: ListView): void {
  const search: Field<HTMLInputElement> = createInputField(t("app.search"), "search", view.filter);
  const archived: Field<HTMLInputElement> = createCheckbox(t("app.showArchived"), view.includeArchived);
  archived.input.addEventListener("change", (): void => {
    navigate(archived.input.checked ? "/groups?archived=1" : "/groups");
  });
  const toolbar: HTMLDivElement = createElement("div", "toolbar");
  toolbar.append(search.wrapper, archived.wrapper);
  if (isAdmin()) {
    toolbar.append(createButton(t("groups.newGroup"), (): void => {
      navigate("/groups/new");
    }, "button-primary"));
  }

  const tableArea: HTMLDivElement = createElement("div", "table-scroll");
  const refresh = (): void => {
    view.onFilter(search.input.value);
    tableArea.replaceChildren(buildTable(view, search.input.value));
  };
  search.input.addEventListener("input", refresh);
  page.append(toolbar, tableArea);
  refresh();
}

function buildTable(view: ListView, filter: string): HTMLElement {
  const rows: Group[] = view.groups
    .filter((group: Group): boolean => matchesSearch(describeForSearch(group, view.reference), filter))
    .sort((first: Group, second: Group): number => createNameComparer(currentLocale())(first.name, second.name));
  if (rows.length === 0) {
    return createElement("p", "muted", t("groups.noGroups"));
  }

  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("groups.name"), t("groups.course"), t("groups.mainTeacher"), t("groups.room"),
    t("groups.slots")]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const group of rows) {
    body.append(buildRow(group, view.reference));
  }

  return table;
}

function buildRow(group: Group, reference: ReferenceData): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  const nameCell: HTMLTableCellElement = createElement("td");
  nameCell.append(createLink(`/groups/${group.id}`, group.name));
  if (group.archived) {
    nameCell.append(" ", createElement("span", "badge badge-neutral", t("app.archived")));
  }

  row.append(nameCell);
  for (const text of [courseName(reference, group.courseId), teacherName(reference, group.currentPeriod.teacherId),
    classroomWithCenter(reference, group.currentPeriod.classroomId), describeSlots(group.currentPeriod.slots)]) {
    row.append(createElement("td", "", text));
  }

  return row;
}

function describeForSearch(group: Group, reference: ReferenceData): string {
  return [group.name, courseName(reference, group.courseId), teacherName(reference,
    group.currentPeriod.teacherId)].join(" ");
}
